import React, { useEffect, useState } from 'react';
import {
  MinusOutlined,
  PlusOutlined,
  PaperClipOutlined,
  UndoOutlined,
} from '@ant-design/icons';

interface PDFToolbarProps {
  scale: number;
  numPages: number;
  currentPage: number;
  isExporting: boolean;
  readOnly?: boolean;
  mode?: 'default' | 'pap-form';
  showExportButton?: boolean;
  canUndo?: boolean;
  onZoomOut: () => void;
  onZoomIn: () => void;
  onResetZoom: () => void;
  onZoomPercentChange: (percent: number) => void;
  onAddImage?: () => void;
  onUndo?: () => void;
  onExport: () => void;
}

const PDFToolbar: React.FC<PDFToolbarProps> = ({
  scale,
  numPages,
  currentPage,
  isExporting,
  readOnly = false,
  mode = 'default',
  showExportButton = true,
  canUndo = false,
  onZoomOut,
  onZoomIn,
  onResetZoom,
  onZoomPercentChange,
  onAddImage,
  onUndo,
  onExport,
}) => {
  const isPapMode = mode === 'pap-form';
  const [zoomInputValue, setZoomInputValue] = useState(
    String(Math.round(scale * 100)),
  );

  useEffect(() => {
    setZoomInputValue(String(Math.round(scale * 100)));
  }, [scale]);

  const applyZoomInputValue = () => {
    const parsedPercent = Number(zoomInputValue);

    if (!Number.isFinite(parsedPercent)) {
      setZoomInputValue(String(Math.round(scale * 100)));
      return;
    }

    onZoomPercentChange(parsedPercent);
  };

  return (
    <div className={`pdf-toolbar ${isPapMode ? 'pdf-toolbar--pap' : ''}`}>
      <div className="pdf-toolbar__left">
        <div className="pdf-toolbar__zoom-group">
          <button
            className="pdf-toolbar__icon-button"
            onClick={onZoomOut}
            disabled={scale <= 0.5}
            aria-label="Zoom Out"
            title="Zoom Out"
          >
            <MinusOutlined className="pdf-toolbar__icon-svg" />
          </button>
          <div className="pdf-toolbar__zoom-input-wrap" title="Set zoom %">
            <input
              type="number"
              min={50}
              max={300}
              step={1}
              className="pdf-toolbar__zoom-input"
              value={zoomInputValue}
              onChange={(event) => setZoomInputValue(event.target.value)}
              onBlur={applyZoomInputValue}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  applyZoomInputValue();
                  (event.target as HTMLInputElement).blur();
                }
                if (event.key === 'Escape') {
                  setZoomInputValue(String(Math.round(scale * 100)));
                  (event.target as HTMLInputElement).blur();
                }
              }}
              aria-label="Zoom percent"
            />
            <span className="pdf-toolbar__zoom-suffix">%</span>
          </div>
          <button
            className="pdf-toolbar__icon-button"
            onClick={onZoomIn}
            disabled={scale >= 3.0}
            aria-label="Zoom In"
            title="Zoom In"
          >
            <PlusOutlined className="pdf-toolbar__icon-svg" />
          </button>
          <button
            className="pdf-toolbar__text-button pdf-toolbar__zoom-value"
            onClick={onResetZoom}
            title="Reset to 100%"
          >
            Reset
          </button>

          {isPapMode && (
            <>
              <div className="pdf-toolbar__separator" aria-hidden="true" />
              <button
                className="pdf-toolbar__icon-button"
                onClick={onAddImage}
                disabled={readOnly || !onAddImage}
                aria-label="Add Image"
                title="Add image"
              >
                <PaperClipOutlined className="pdf-toolbar__icon-svg" />
              </button>
              <button
                className="pdf-toolbar__icon-button"
                onClick={onUndo}
                disabled={!canUndo || readOnly || !onUndo}
                aria-label="Undo"
                title="Undo"
              >
                <UndoOutlined className="pdf-toolbar__icon-svg" />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="pdf-toolbar__center">
        <div className="pdf-toolbar__page">
          <span className="pdf-toolbar__page-current">
            {numPages ? currentPage : '--'}
          </span>
          <span className="pdf-toolbar__page-divider">/</span>
          <span className="pdf-toolbar__page-total">{numPages || '--'}</span>
        </div>
        <div className="pdf-toolbar__separator" aria-hidden="true" />
        <span className="pdf-toolbar__hint">
          {isPapMode
            ? 'Fill text/checkbox/radio directly on PDF'
            : 'Ctrl/Cmd+Scroll to zoom'}
        </span>
      </div>

      <div className="pdf-toolbar__right">
        {showExportButton && (
          <button
            onClick={onExport}
            disabled={isExporting}
            className="pdf-toolbar__primary-button pdf-toolbar__primary-button--icon"
            title="Download PDF"
            aria-label="Download PDF"
          >
            {isExporting ? (
              <span className="pdf-toolbar__primary-label">Exporting...</span>
            ) : (
              <svg
                className="pdf-toolbar__download-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M12 3a1 1 0 0 1 1 1v8.59l2.3-2.3a1 1 0 1 1 1.4 1.42l-4 3.99a1 1 0 0 1-1.4 0l-4-3.99a1 1 0 1 1 1.4-1.42l2.3 2.3V4a1 1 0 0 1 1-1Zm-7 14a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1Z"
                />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default PDFToolbar;
